import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import CustomerLayout from '../components/CustomerLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Store, MapPin, Phone, Mail, Star, Search, Grid3X3, List, 
  Package, Truck, Shield, Clock, ChevronRight, QrCode, Share2
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { formatPrice } from '../lib/utils';

export default function VendorStorePage() {
  const { vendorId } = useParams();
  const [vendor, setVendor] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('shop');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchVendorData();
  }, [vendorId]);

  const fetchVendorData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/vendors/${vendorId}/public`);
      setVendor(response.data.vendor);
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Failed to fetch vendor data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort products
  const filteredProducts = products
    .filter(p => !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return (a.sale_price || a.price) - (b.sale_price || b.price);
        case 'price-high':
          return (b.sale_price || b.price) - (a.sale_price || a.price);
        case 'name':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

  if (loading) {
    return (
      <CustomerLayout>
        <div className="min-h-screen flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      </CustomerLayout>
    );
  }

  if (!vendor) {
    return (
      <CustomerLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Store Not Found</h2>
            <p className="text-gray-500 mb-4">This vendor store doesn't exist or is not active.</p>
            <Link to="/products" className="text-emerald-600 hover:underline">Browse All Products</Link>
          </div>
        </div>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout>
      {/* Store Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            {/* Store Logo */}
            <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-emerald-100 to-green-100 flex items-center justify-center overflow-hidden border-2 border-white shadow-lg">
              {vendor.logo ? (
                <img src={vendor.logo} alt={vendor.business_name} className="w-full h-full object-cover" />
              ) : (
                <Store className="w-12 h-12 text-emerald-600" />
              )}
            </div>

            {/* Store Info */}
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900 mb-1">{vendor.business_name}</h1>
              <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {vendor.address?.city || 'India'}, {vendor.address?.state || ''}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                  <Truck className="w-3 h-3 mr-1" /> Deliverable
                </Badge>
                <Badge variant="outline" className="text-green-700 border-green-300">
                  <Clock className="w-3 h-3 mr-1" /> Open
                </Badge>
              </div>
            </div>

            {/* QR Code placeholder */}
            <div className="hidden md:flex flex-col items-center p-4 bg-gray-50 rounded-xl">
              <div className="w-20 h-20 bg-white rounded-lg flex items-center justify-center border">
                <QrCode className="w-12 h-12 text-gray-300" />
              </div>
              <span className="text-xs text-gray-500 mt-2">Download QR</span>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-6 mt-6 border-t pt-4">
            <button
              onClick={() => setActiveTab('shop')}
              className={`pb-2 font-medium transition-colors ${
                activeTab === 'shop' 
                  ? 'text-emerald-600 border-b-2 border-emerald-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Shop
            </button>
            <button
              onClick={() => setActiveTab('about')}
              className={`pb-2 font-medium transition-colors ${
                activeTab === 'about' 
                  ? 'text-emerald-600 border-b-2 border-emerald-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              About
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {activeTab === 'shop' ? (
          <>
            {/* Product Count & Controls */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <p className="text-gray-600">
                Showing <span className="font-semibold text-gray-900">{filteredProducts.length}</span> products
              </p>
              
              <div className="flex items-center gap-3">
                {/* Search */}
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Sort */}
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Sort By" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest</SelectItem>
                    <SelectItem value="price-low">Price: Low to High</SelectItem>
                    <SelectItem value="price-high">Price: High to Low</SelectItem>
                    <SelectItem value="name">Name</SelectItem>
                  </SelectContent>
                </Select>

                {/* View Mode */}
                <div className="flex border rounded-lg">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 ${viewMode === 'grid' ? 'bg-emerald-100 text-emerald-700' : 'text-gray-500'}`}
                  >
                    <Grid3X3 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 ${viewMode === 'list' ? 'bg-emerald-100 text-emerald-700' : 'text-gray-500'}`}
                  >
                    <List className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Products Grid/List */}
            {filteredProducts.length > 0 ? (
              <div className={
                viewMode === 'grid' 
                  ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4' 
                  : 'space-y-4'
              }>
                {filteredProducts.map((product) => (
                  <ProductCard 
                    key={product.id || product._id} 
                    product={product} 
                    vendor={vendor}
                    viewMode={viewMode}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No products found</h3>
                <p className="text-gray-500">Try adjusting your search or filters.</p>
              </div>
            )}
          </>
        ) : (
          /* About Tab */
          <div className="max-w-2xl">
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">About {vendor.business_name}</h2>
                
                {vendor.description && (
                  <p className="text-gray-700 mb-6 leading-relaxed">{vendor.description}</p>
                )}

                <div className="space-y-4">
                  {vendor.address && (
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-emerald-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-gray-900">Location</h4>
                        <p className="text-gray-600 text-sm">
                          {vendor.address.street && `${vendor.address.street}, `}
                          {vendor.address.city}, {vendor.address.state} - {vendor.address.pincode}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-emerald-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900">Trust & Safety</h4>
                      <p className="text-gray-600 text-sm">
                        Verified seller on nammahut marketplace. All products are quality checked.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Truck className="w-5 h-5 text-emerald-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900">Delivery</h4>
                      <p className="text-gray-600 text-sm">
                        Ships to all India. International delivery available for select products.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t">
                  <h4 className="font-medium text-gray-900 mb-3">Quick Stats</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold text-emerald-600">{products.length}</div>
                      <div className="text-xs text-gray-500">Products</div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold text-emerald-600">4.5</div>
                      <div className="text-xs text-gray-500">Rating</div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold text-emerald-600">100%</div>
                      <div className="text-xs text-gray-500">Positive</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </CustomerLayout>
  );
}

// Product Card Component
function ProductCard({ product, vendor, viewMode }) {
  const hasDiscount = product.sale_price && product.sale_price < product.price;
  const discountPercent = hasDiscount ? Math.round((1 - product.sale_price / product.price) * 100) : 0;
  
  // Use slug for SEO-friendly URLs
  const productUrl = product.slug || product.id || product._id;

  if (viewMode === 'list') {
    return (
      <Link to={`/products/${productUrl}`}>
        <Card className="overflow-hidden hover:shadow-lg transition-all">
          <div className="flex">
            <div className="w-40 h-40 bg-gray-100 flex-shrink-0 relative">
              <img 
                src={product.images?.[0] || 'https://via.placeholder.com/200?text=nammahut'} 
                alt={product.name} 
                className="w-full h-full object-cover" 
              />
              {hasDiscount && (
                <Badge className="absolute top-2 left-2 bg-red-500 text-white text-xs">
                  {discountPercent}% OFF
                </Badge>
              )}
            </div>
            <CardContent className="flex-1 p-4">
              <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2">{product.name}</h3>
              <p className="text-sm text-gray-500 mb-2 line-clamp-2">{product.description}</p>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xl font-bold text-gray-900">
                  ₹{(product.sale_price || product.price).toLocaleString()}
                </span>
                {hasDiscount && (
                  <span className="text-sm text-gray-400 line-through">
                    ₹{product.price.toLocaleString()}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  <Truck className="w-3 h-3 mr-1" /> Free Shipping
                </Badge>
              </div>
            </CardContent>
          </div>
        </Card>
      </Link>
    );
  }

  return (
    <Link to={`/products/${productUrl}`} className="group">
      <Card className="overflow-hidden h-full border hover:shadow-lg transition-all">
        <div className="relative aspect-square bg-gray-100">
          <img 
            src={product.images?.[0] || 'https://via.placeholder.com/200?text=nammahut'} 
            alt={product.name} 
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
          />
          {hasDiscount && (
            <Badge className="absolute top-2 left-2 bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-bold">
              {discountPercent}% OFF
            </Badge>
          )}
          {/* Quick add button */}
          <Button 
            size="sm"
            className="absolute bottom-2 left-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-emerald-600 hover:bg-emerald-700"
          >
            ADD TO CART
          </Button>
        </div>
        <CardContent className="p-3">
          <h3 className="font-medium text-gray-800 text-sm line-clamp-2 mb-1 group-hover:text-emerald-700 leading-tight">
            {product.name}
          </h3>
          <p className="text-xs text-gray-500 mb-2">Seller: {vendor?.business_name}</p>
          <div className="flex items-baseline gap-2">
            <span className="font-bold text-gray-900">₹{(product.sale_price || product.price).toLocaleString()}</span>
            {hasDiscount && (
              <>
                <span className="text-xs text-gray-400 line-through">₹{product.price.toLocaleString()}</span>
                <span className="text-xs text-red-500 font-medium">({discountPercent}% OFF)</span>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
